import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon, Table as TableIcon, Minus, Undo, Redo,
  X, Save, Palette, ChevronDown, Variable, FileSignature, Users, Pencil,
  Plus, Trash2, Mail, MessageCircle, Brain, Maximize2, GitBranch, Check,
  Rows3, Columns3, Indent, Outdent, ZoomIn, ZoomOut, FileText,
} from "lucide-react";

const indentNode = (editor, delta) => {
  if (!editor) return;
  const { state, view } = editor;
  const { from, to } = state.selection;
  const tr = state.tr;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === "paragraph" || node.type.name === "heading") {
      const current = node.attrs.indent || 0;
      const next = Math.max(0, Math.min(10, current + delta));
      if (next !== current) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
    }
  });
  view.dispatch(tr);
};

const IndentExtension = Extension.create({
  name: "customFormatting",
  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          indent: {
            default: 0,
            parseHTML: el => parseInt(el.style.paddingLeft || "0", 10) / 24 || 0,
            renderHTML: () => ({}),
          },
          lineHeight: {
            default: null,
            parseHTML: el => el.style.lineHeight ? parseFloat(el.style.lineHeight) || null : null,
            renderHTML: (attrs) => {
              const parts = [];
              if (attrs.indent > 0) parts.push(`padding-left: ${attrs.indent * 24}px`);
              if (attrs.lineHeight) parts.push(`line-height: ${attrs.lineHeight}`);
              return parts.length ? { style: parts.join("; ") } : {};
            },
          },
        },
      },
    ];
  },
});
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import { useAppearance } from "../contexts/AppearanceContext";
import { applyMask } from "./PhoneInput";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#000000", "#1e293b", "#374151", "#dc2626", "#ea580c", "#d97706", "#16a34a", "#2563eb", "#7c3aed", "#db2777"];
const VARIABLES = {
  "Negócio": ["titulo_negocio", "valor_setup", "valor_mrr", "valor_negocio", "valor_tcv", "data_hoje"],
  "Contato": ["nome_contato", "email_contato", "telefone"],
  "Empresa": ["empresa", "cnpj", "endereco"],
  "Dados Conferidos": [
    "dconf_razao_social", "dconf_nome_fantasia", "dconf_cnpj",
    "dconf_logradouro", "dconf_numero", "dconf_complemento", "dconf_bairro", "dconf_municipio", "dconf_uf", "dconf_cep",
    "dconf_nome", "dconf_cpf", "dconf_data_nascimento",
  ],
};
const AUTOSAVE_INTERVAL = 30000; // 30 seconds

export default function ContractTemplateEditor({ open, onClose, template, onSave, aiVariables = [] }) {
  const appearance = useAppearance();
  const phoneMask = appearance.phone_mask || "(##) #####-####";
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [signers, setSigners] = useState([]);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [signersDialogOpen, setSignersDialogOpen] = useState(false);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [promptFullscreen, setPromptFullscreen] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [customFieldVars, setCustomFieldVars] = useState([]);
  const [varSearch, setVarSearch] = useState("");
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [contractRule, setContractRule] = useState("none");
  const [allPipelines, setAllPipelines] = useState([]);
  const [allStagesMap, setAllStagesMap] = useState({});
  const [isInTable, setIsInTable] = useState(false);
  const [isOnImage, setIsOnImage] = useState(false);
  const [floatingPos, setFloatingPos] = useState(null);
  const editorContainerRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const createdIdRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle, Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: { default: null, parseHTML: el => el.getAttribute("width") || el.style.width || null, renderHTML: attrs => attrs.width ? { width: attrs.width, style: `width: ${attrs.width}` } : {} },
            alignment: { default: "left", parseHTML: el => el.getAttribute("data-alignment") || "left", renderHTML: attrs => {
              const style = attrs.alignment === "center" ? "display: block; margin-left: auto; margin-right: auto;" : attrs.alignment === "right" ? "display: block; margin-left: auto; margin-right: 0;" : "";
              return style ? { "data-alignment": attrs.alignment, style: `${attrs.width ? `width: ${attrs.width};` : ""} ${style}`.trim() } : attrs.width ? { width: attrs.width, style: `width: ${attrs.width}` } : {};
            }},
          };
        },
      }).configure({ inline: false }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
      IndentExtension,
    ],
    content: "",
    onSelectionUpdate: ({ editor: ed }) => {
      const inTable = ed.isActive("table");
      const onImage = ed.isActive("image");
      setIsInTable(inTable);
      setIsOnImage(onImage);
      if ((inTable || onImage) && editorContainerRef.current) {
        try {
          const { from } = ed.state.selection;
          const domAtPos = ed.view.domAtPos(from);
          const node = domAtPos.node?.nodeType === 1 ? domAtPos.node : domAtPos.node?.parentElement;
          const target = onImage ? node?.closest("img") || node : node?.closest("table") || node;
          if (target) {
            const containerRect = editorContainerRef.current.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            setFloatingPos({
              top: targetRect.top - containerRect.top - 40,
              left: targetRect.left - containerRect.left + targetRect.width / 2,
            });
            return;
          }
        } catch {}
      }
      setFloatingPos(null);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-full px-16 py-12 text-slate-800",
        style: "font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 14px; line-height: 1.8;",
      },
    },
  });

  useEffect(() => {
    if (open && template) {
      createdIdRef.current = template.id || null;
      setName(template.name || "");
      setSigners(template.signers || []);
      setRequiredDocs(template.required_documents || []);
      setAiEnabled(template.ai_validation_enabled || false);
      setAiPrompt(template.ai_prompt || "");
      setPipelineStages(template.pipeline_stages || []);
      setContractRule(template.contract_rule || "none");
      if (editor) editor.commands.setContent(template.html_body || "");
      // Fetch custom fields
      axios.get(`${API}/custom-fields?module=negocios`).then(r => {
        const fields = r.data || [];
        setCustomFieldVars(fields.map(f => ({ key: `campo_${f.field_name}`, label: f.field_label, type: f.field_type || "text" })));
      }).catch(() => setCustomFieldVars([]));
      // Fetch pipelines + stages for rules config
      axios.get(`${API}/pipelines`).then(r => {
        const pipes = r.data || [];
        setAllPipelines(pipes);
        pipes.forEach(p => {
          axios.get(`${API}/pipeline-stages?pipeline_id=${p.id}`).then(sr => {
            setAllStagesMap(prev => ({ ...prev, [p.id]: sr.data || [] }));
          }).catch(() => {});
        });
      }).catch(() => setAllPipelines([]));
    }
  }, [open, template]); // eslint-disable-line

  // Autosave
  const doSave = useCallback(async (silent = false) => {
    if (!editor || !name.trim()) return;
    const html = editor.getHTML();
    const payload = { ...template, name, signers, html_body: html, required_documents: requiredDocs, ai_validation_enabled: aiEnabled, ai_prompt: aiPrompt, pipeline_stages: pipelineStages, contract_rule: contractRule };
    const existingId = createdIdRef.current || payload.id;
    try {
      if (existingId) {
        payload.id = existingId;
        await axios.put(`${API}/autentique/templates/${existingId}`, payload);
      } else {
        const r = await axios.post(`${API}/autentique/templates`, payload);
        if (r.data?.id) {
          createdIdRef.current = r.data.id;
          payload.id = r.data.id;
        }
      }
      setLastSaved(new Date());
    } catch {
      if (!silent) toast.error("Erro ao salvar");
    }
  }, [editor, name, signers, template, requiredDocs, aiEnabled, aiPrompt, pipelineStages, contractRule]);

  useEffect(() => {
    if (!open) return;
    autoSaveTimer.current = setInterval(() => {
      if (name.trim()) doSave(true);
    }, AUTOSAVE_INTERVAL);
    return () => { if (autoSaveTimer.current) clearInterval(autoSaveTimer.current); };
  }, [open, doSave, name]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Informe o nome do modelo"); setNameDialogOpen(true); return; }
    setSaving(true);
    await doSave(false);
    setSaving(false);
  };

  const handleClose = async () => {
    if (name.trim() && editor) await doSave(true);
    onClose();
  };

  const insertVariable = useCallback((varName) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{${varName}}`).run();
  }, [editor]);

  // Block ESC from closing the editor
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => { if (e.key === "Escape") e.stopPropagation(); };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open]);

  if (!open) return null;

  const ToolBtn = ({ onClick, active, children, title }) => (
    <button onClick={onClick} title={title}
      className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${active ? "bg-slate-200 text-slate-900" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"}`}>
      {children}
    </button>
  );
  const Sep = () => <div className="w-px h-5 bg-slate-200 mx-0.5" />;

  return createPortal(
    <div className="fixed inset-0 z-[99999] bg-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-1.5 cursor-pointer hover:bg-slate-50 rounded px-2 py-1 transition-colors" onClick={() => setNameDialogOpen(true)}>
            <FileSignature className="w-4 h-4" style={{ color: tc500 }} />
            <span className="text-sm font-semibold text-slate-800 max-w-[300px] truncate">{name || "Sem nome"}</span>
            <Pencil className="w-3 h-3 text-slate-400" />
          </div>
          {lastSaved && <span className="text-[10px] text-slate-400">Salvo às {lastSaved.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => setNameDialogOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"><Pencil className="w-4 h-4" /></button>
          </TooltipTrigger><TooltipContent>Nome do modelo</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => setSignersDialogOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 relative">
              <Users className="w-4 h-4" />
              {signers.filter(s => s.name || s.email || s.phone).length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ backgroundColor: tc500 }}>{signers.filter(s => s.name || s.email || s.phone).length}</span>}
            </button>
          </TooltipTrigger><TooltipContent>Signatários ({signers.filter(s => s.name || s.email || s.phone).length})</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => setDocsDialogOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 relative">
              <FileText className="w-4 h-4" />
              {requiredDocs.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ backgroundColor: tc500 }}>{requiredDocs.length}</span>}
            </button>
          </TooltipTrigger><TooltipContent>Documentos obrigatórios ({requiredDocs.length})</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => setAiDialogOpen(true)} className={`w-9 h-9 flex items-center justify-center rounded-lg border hover:bg-slate-50 ${aiEnabled ? "border-slate-300 text-slate-700" : "border-slate-200 text-slate-500"}`} style={aiEnabled ? { backgroundColor: tc50, borderColor: tc500 + "60", color: tc500 } : {}}>
              <Brain className="w-4 h-4" />
            </button>
          </TooltipTrigger><TooltipContent>Validação IA {aiEnabled ? "(ativa)" : "(inativa)"}</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <button onClick={() => setRulesDialogOpen(true)} className={`w-9 h-9 flex items-center justify-center rounded-lg border hover:bg-slate-50 ${pipelineStages.length > 0 ? "border-slate-300 text-slate-700" : "border-slate-200 text-slate-500"}`} style={pipelineStages.length > 0 ? { backgroundColor: tc50, borderColor: tc500 + "60", color: tc500 } : {}}>
              <GitBranch className="w-4 h-4" />
            </button>
          </TooltipTrigger><TooltipContent>Regras de Pipeline {pipelineStages.length > 0 ? `(${pipelineStages.reduce((a, p) => a + (p.stage_ids?.length || 0), 0)} etapas)` : ""}</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild>
            <button onClick={handleSave} disabled={saving} className="w-9 h-9 flex items-center justify-center rounded-lg border text-white disabled:opacity-50" style={{ backgroundColor: tc500, borderColor: tc500 }}>
              {saving ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
          </TooltipTrigger><TooltipContent>Salvar</TooltipContent></Tooltip>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-4 py-1.5 border-b border-slate-200 bg-slate-50 flex-shrink-0 flex-wrap">
        <ToolBtn onClick={() => editor?.chain().focus().undo().run()} title="Desfazer"><Undo className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().redo().run()} title="Refazer"><Redo className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive("bold")} title="Negrito"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive("italic")} title="Itálico"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive("underline")} title="Sublinhado"><UnderlineIcon className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().toggleStrike().run()} active={editor?.isActive("strike")} title="Riscado"><Strikethrough className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()} active={editor?.isActive("heading", { level: 1 })} title="Título 1"><Heading1 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive("heading", { level: 2 })} title="Título 2"><Heading2 className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()} active={editor?.isActive("heading", { level: 3 })} title="Título 3"><Heading3 className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive("bulletList")} title="Lista"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive("orderedList")} title="Lista numerada"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => editor?.chain().focus().setTextAlign("left").run()} active={editor?.isActive({ textAlign: "left" })} title="Esquerda"><AlignLeft className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().setTextAlign("center").run()} active={editor?.isActive({ textAlign: "center" })} title="Centro"><AlignCenter className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().setTextAlign("right").run()} active={editor?.isActive({ textAlign: "right" })} title="Direita"><AlignRight className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().setTextAlign("justify").run()} active={editor?.isActive({ textAlign: "justify" })} title="Justificar"><AlignJustify className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => indentNode(editor, -1)} title="Diminuir recuo"><Outdent className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => indentNode(editor, 1)} title="Aumentar recuo"><Indent className="w-3.5 h-3.5" /></ToolBtn>
        <Popover>
          <PopoverTrigger asChild>
            <button className="w-8 h-8 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100" title="Espaçamento entre linhas"><Rows3 className="w-3.5 h-3.5" /></button>
          </PopoverTrigger>
          <PopoverContent className="w-32 p-1.5 z-[999999]" align="start" sideOffset={8}>
            <p className="text-[9px] text-slate-400 font-semibold uppercase px-2 py-1">Espaçamento</p>
            {[1, 1.15, 1.5, 1.8, 2, 2.5, 3].map(lh => (
              <button key={lh} onMouseDown={e => {
                e.preventDefault();
                if (!editor) return;
                const { state, view } = editor;
                const { from, to } = state.selection;
                const tr = state.tr;
                state.doc.nodesBetween(from, to, (node, pos) => {
                  if (node.type.name === "paragraph" || node.type.name === "heading") {
                    tr.setNodeMarkup(pos, undefined, { ...node.attrs, lineHeight: lh });
                  }
                });
                view.dispatch(tr);
              }} className="w-full text-left px-2 py-1.5 rounded text-xs text-slate-700 hover:bg-slate-100">{lh}x</button>
            ))}
          </PopoverContent>
        </Popover>
        <Sep />
        {/* Text color */}
        <Popover>
          <PopoverTrigger asChild><button className="w-8 h-8 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100" title="Cor do texto"><Palette className="w-3.5 h-3.5" /></button></PopoverTrigger>
          <PopoverContent className="w-auto p-2 z-[999999]" align="start" sideOffset={8}>
            <p className="text-[9px] text-slate-400 mb-1.5">Cor do texto</p>
            <div className="flex gap-1 flex-wrap max-w-[160px]">
              {COLORS.map(c => (<button key={c} onMouseDown={e => { e.preventDefault(); editor?.chain().focus().setColor(c).run(); }} className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform" style={{ backgroundColor: c }} />))}
              <button onMouseDown={e => { e.preventDefault(); editor?.chain().focus().unsetColor().run(); }} className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform text-[8px] text-slate-400">✕</button>
            </div>
            <p className="text-[9px] text-slate-400 mt-2 mb-1.5">Fundo do texto</p>
            <div className="flex gap-1 flex-wrap max-w-[160px]">
              {["#fef3c7", "#fce7f3", "#dbeafe", "#dcfce7", "#f3e8ff", "#fee2e2", "#e0f2fe", "#fef9c3", "#f1f5f9"].map(c => (<button key={c} onMouseDown={e => { e.preventDefault(); editor?.chain().focus().toggleHighlight({ color: c }).run(); }} className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform" style={{ backgroundColor: c }} />))}
              <button onMouseDown={e => { e.preventDefault(); editor?.chain().focus().unsetHighlight().run(); }} className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform text-[8px] text-slate-400">✕</button>
            </div>
          </PopoverContent>
        </Popover>
        <Sep />
        <ToolBtn onClick={() => { const url = window.prompt("URL:"); if (url) editor?.chain().focus().setLink({ href: url }).run(); }} active={editor?.isActive("link")} title="Link"><LinkIcon className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => { const url = window.prompt("URL da imagem:"); if (url) editor?.chain().focus().setImage({ src: url }).run(); }} title="Imagem"><ImageIcon className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => { if (!editor?.isActive("table")) editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); }} active={editor?.isActive("table")} title="Inserir tabela"><TableIcon className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => editor?.chain().focus().setHorizontalRule().run()} title="Linha"><Minus className="w-3.5 h-3.5" /></ToolBtn>
        <Sep />
        <Popover>
          <PopoverTrigger asChild>
            <button className="h-8 px-2.5 flex items-center gap-1 rounded text-xs font-medium transition-colors" style={{ color: tc500, backgroundColor: tc50 }}>
              <Variable className="w-3.5 h-3.5" />Variáveis <ChevronDown className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 z-[999999]" align="start">
            <div className="p-2 border-b border-slate-100">
              <Input
                placeholder="Buscar variável..."
                className="h-7 text-xs"
                value={varSearch}
                onChange={e => setVarSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto p-1.5 space-y-1.5">
              {Object.entries(VARIABLES).map(([group, vars]) => {
                const filtered = vars.filter(v => !varSearch || v.toLowerCase().includes(varSearch.toLowerCase()));
                if (filtered.length === 0) return null;
                return (
                  <div key={group}>
                    <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider px-1.5 mb-0.5">{group}</p>
                    {filtered.map(v => (<button key={v} onClick={() => { insertVariable(v); setVarSearch(""); }} className="w-full text-left px-2 py-1 rounded text-xs font-mono text-slate-700 hover:bg-slate-100 whitespace-nowrap overflow-hidden text-ellipsis">{`{${v}}`}</button>))}
                  </div>
                );
              })}
              {aiVariables.length > 0 && (() => {
                const filtered = aiVariables.filter(v => !varSearch || v.key.toLowerCase().includes(varSearch.toLowerCase()));
                if (filtered.length === 0) return null;
                return (
                  <div>
                    <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider px-1.5 mb-0.5">Validação IA</p>
                    {filtered.map(v => (<button key={v.key} onClick={() => { insertVariable(v.key); setVarSearch(""); }} className="w-full text-left px-2 py-1 rounded text-xs font-mono text-purple-600 hover:bg-purple-50 whitespace-nowrap overflow-hidden text-ellipsis">{`{${v.key}}`}</button>))}
                  </div>
                );
              })()}
              {customFieldVars.length > 0 && (() => {
                const filtered = customFieldVars.filter(v => !varSearch || v.key.toLowerCase().includes(varSearch.toLowerCase()) || v.label.toLowerCase().includes(varSearch.toLowerCase()));
                if (filtered.length === 0) return null;
                return (
                  <div>
                    <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider px-1.5 mb-0.5">Campos Personalizados</p>
                    {filtered.map(v => (
                      <button key={v.key} onClick={() => { insertVariable(v.key); setVarSearch(""); }} title={`${v.label} · ${v.type}`} className="w-full text-left px-2 py-1 rounded text-xs font-mono text-orange-600 hover:bg-orange-50 whitespace-nowrap overflow-hidden text-ellipsis">{`{${v.key}}`}</button>
                    ))}
                  </div>
                );
              })()}
              {varSearch && Object.values(VARIABLES).flat().filter(v => v.toLowerCase().includes(varSearch.toLowerCase())).length === 0 &&
                aiVariables.filter(v => v.key.toLowerCase().includes(varSearch.toLowerCase())).length === 0 &&
                customFieldVars.filter(v => v.key.toLowerCase().includes(varSearch.toLowerCase()) || v.label.toLowerCase().includes(varSearch.toLowerCase())).length === 0 && (
                <p className="text-xs text-slate-400 text-center py-3">Nenhuma variável encontrada</p>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-auto bg-slate-100 relative" ref={editorContainerRef}>
        {/* Floating Table toolbar */}
        {isInTable && editor && floatingPos && (
          <div className="absolute z-20" style={{ top: Math.max(0, floatingPos.top), left: Math.max(8, floatingPos.left - 200) }}>
            <div className="flex items-center gap-0.5 bg-white rounded-lg shadow-xl border border-slate-200 px-1.5 py-1">
              <span className="text-[9px] text-slate-400 font-semibold uppercase px-1">Col</span>
              <button onClick={() => editor.chain().focus().addColumnBefore().run()} title="Coluna à esquerda"
                className="h-7 px-1.5 flex items-center gap-1 rounded text-[11px] text-slate-600 hover:bg-slate-100 transition-colors">
                <Plus className="w-3 h-3" />Antes
              </button>
              <button onClick={() => editor.chain().focus().addColumnAfter().run()} title="Coluna à direita"
                className="h-7 px-1.5 flex items-center gap-1 rounded text-[11px] text-slate-600 hover:bg-slate-100 transition-colors">
                <Plus className="w-3 h-3" />Depois
              </button>
              <button onClick={() => editor.chain().focus().deleteColumn().run()} title="Excluir coluna"
                className="h-7 px-1.5 flex items-center rounded text-[11px] text-rose-500 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="w-px h-5 bg-slate-200 mx-0.5" />
              <span className="text-[9px] text-slate-400 font-semibold uppercase px-1">Linha</span>
              <button onClick={() => editor.chain().focus().addRowBefore().run()} title="Linha acima"
                className="h-7 px-1.5 flex items-center gap-1 rounded text-[11px] text-slate-600 hover:bg-slate-100 transition-colors">
                <Plus className="w-3 h-3" />Acima
              </button>
              <button onClick={() => editor.chain().focus().addRowAfter().run()} title="Linha abaixo"
                className="h-7 px-1.5 flex items-center gap-1 rounded text-[11px] text-slate-600 hover:bg-slate-100 transition-colors">
                <Plus className="w-3 h-3" />Abaixo
              </button>
              <button onClick={() => editor.chain().focus().deleteRow().run()} title="Excluir linha"
                className="h-7 px-1.5 flex items-center rounded text-[11px] text-rose-500 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="w-px h-5 bg-slate-200 mx-0.5" />
              <button onClick={() => editor.chain().focus().deleteTable().run()} title="Excluir tabela"
                className="h-7 px-1.5 flex items-center gap-1 rounded text-[11px] text-rose-500 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-3 h-3" />Tabela
              </button>
            </div>
          </div>
        )}
        {/* Floating Image toolbar */}
        {isOnImage && editor && floatingPos && (
          <div className="absolute z-20" style={{ top: Math.max(0, floatingPos.top), left: Math.max(8, floatingPos.left - 180) }}>
            <div className="flex items-center gap-0.5 bg-white rounded-lg shadow-xl border border-slate-200 px-1.5 py-1">
              {["25%", "50%", "75%", "100%"].map(w => (
                <button key={w} onClick={() => editor.chain().focus().updateAttributes("image", { width: w }).run()}
                  className={`h-7 px-2 rounded text-[11px] font-medium transition-colors ${editor.getAttributes("image").width === w ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:bg-slate-100"}`}>
                  {w}
                </button>
              ))}
              <button onClick={() => { const curr = parseInt(editor.getAttributes("image").width || "100", 10); if (curr > 10) editor.chain().focus().updateAttributes("image", { width: `${curr - 10}%` }).run(); }}
                title="Diminuir" className="h-7 px-1 flex items-center rounded text-slate-500 hover:bg-slate-100 transition-colors">
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => { const curr = parseInt(editor.getAttributes("image").width || "100", 10); if (curr < 100) editor.chain().focus().updateAttributes("image", { width: `${Math.min(100, curr + 10)}%` }).run(); }}
                title="Aumentar" className="h-7 px-1 flex items-center rounded text-slate-500 hover:bg-slate-100 transition-colors">
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-5 bg-slate-200 mx-0.5" />
              <button onClick={() => editor.chain().focus().updateAttributes("image", { alignment: "left" }).run()}
                className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${editor.getAttributes("image").alignment !== "center" && editor.getAttributes("image").alignment !== "right" ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:bg-slate-100"}`} title="Esquerda">
                <AlignLeft className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => editor.chain().focus().updateAttributes("image", { alignment: "center" }).run()}
                className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${editor.getAttributes("image").alignment === "center" ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:bg-slate-100"}`} title="Centro">
                <AlignCenter className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => editor.chain().focus().updateAttributes("image", { alignment: "right" }).run()}
                className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${editor.getAttributes("image").alignment === "right" ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:bg-slate-100"}`} title="Direita">
                <AlignRight className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-5 bg-slate-200 mx-0.5" />
              <button onClick={() => editor.chain().focus().deleteSelection().run()} title="Excluir imagem"
                className="h-7 px-1.5 flex items-center rounded text-[11px] text-rose-500 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
        <div className="max-w-[850px] mx-auto my-8 bg-white shadow-lg rounded-sm min-h-[calc(100vh-200px)]">
          <EditorContent editor={editor} className="min-h-full" />
        </div>
      </div>

      {/* Name Dialog */}
      {nameDialogOpen && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => setNameDialogOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><FileSignature className="w-4 h-4" style={{ color: tc500 }} />Nome do modelo</p>
              <button onClick={() => setNameDialogOpen(false)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Contrato de Prestação de Serviços" autoFocus />
              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} onClick={() => setNameDialogOpen(false)}>Confirmar</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Signers Dialog */}
      {signersDialogOpen && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => setSignersDialogOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 sticky top-0 bg-white">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Users className="w-4 h-4" style={{ color: tc500 }} />Signatários padrão</p>
              <button onClick={() => setSignersDialogOpen(false)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-slate-500">Adicione aqui os signatários padrão para este modelo de contrato.</p>
              {signers.map((s, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input value={s.name || ""} onChange={e => { const u = [...signers]; u[i] = { ...u[i], name: e.target.value }; setSigners(u); }} placeholder="Nome completo" className="flex-1 h-8 text-xs" />
                    <button onClick={() => { const u = [...signers]; u[i] = { ...u[i], delivery_method: s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? "" : "DELIVERY_METHOD_WHATSAPP" }; setSigners(u); }}
                      className={`h-8 px-2 rounded-md border flex items-center gap-1 text-[10px] font-medium flex-shrink-0 transition-colors ${s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-blue-300 bg-blue-50 text-blue-700"}`}>
                      {s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? <><MessageCircle className="w-3 h-3" />WhatsApp</> : <><Mail className="w-3 h-3" />E-mail</>}
                    </button>
                    {signers.length > 1 && <button onClick={() => setSigners(prev => prev.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                  {s.delivery_method === "DELIVERY_METHOD_WHATSAPP" ? (
                    <Input
                      value={s.phone ? applyMask(s.phone.replace(/\D/g, "").replace(/^55/, ""), phoneMask) : ""}
                      onChange={e => { const u = [...signers]; u[i] = { ...u[i], phone: e.target.value.replace(/\D/g, "") }; setSigners(u); }}
                      placeholder={applyMask("11999999999", phoneMask)} className="h-8 text-xs" />
                  ) : (
                    <Input value={s.email || ""} onChange={e => { const u = [...signers]; u[i] = { ...u[i], email: e.target.value }; setSigners(u); }} placeholder="E-mail" className="h-8 text-xs" />
                  )}
                  {i < signers.length - 1 && <div className="border-b border-slate-100" />}
                </div>
              ))}
              <button onClick={() => setSigners(prev => [...prev, { name: "", email: "", action: "SIGN", phone: "", delivery_method: "" }])}
                className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-xs text-slate-400 hover:text-slate-600 hover:border-slate-400 flex items-center justify-center gap-1">
                <Plus className="w-3 h-3" />Adicionar signatário
              </button>
              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} onClick={() => setSignersDialogOpen(false)}>Confirmar</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Documents Dialog */}
      {docsDialogOpen && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => setDocsDialogOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 sticky top-0 bg-white">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><FileText className="w-4 h-4" style={{ color: tc500 }} />Documentos obrigatórios</p>
              <button onClick={() => setDocsDialogOpen(false)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-slate-500">Defina os documentos que devem ser enviados antes de emitir este contrato.</p>
              {requiredDocs.map((doc, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={doc.name || ""} onChange={e => { const d = [...requiredDocs]; d[i] = { ...d[i], name: e.target.value }; setRequiredDocs(d); }} placeholder="Nome do documento (ex: Contrato Social)" className="flex-1 h-8 text-xs" />
                  <button onClick={() => setRequiredDocs(prev => prev.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => setRequiredDocs(prev => [...prev, { name: "", required: true }])}
                className="w-full py-2 border border-dashed border-slate-300 rounded-lg text-xs text-slate-400 hover:text-slate-600 hover:border-slate-400 flex items-center justify-center gap-1">
                <Plus className="w-3 h-3" />Adicionar documento
              </button>
              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} onClick={() => setDocsDialogOpen(false)}>Confirmar</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Validation Dialog */}
      {aiDialogOpen && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => { setAiDialogOpen(false); setPromptFullscreen(false); }}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 sticky top-0 bg-white">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Brain className="w-4 h-4" style={{ color: tc500 }} />Validação por IA</p>
              <button onClick={() => { setAiDialogOpen(false); setPromptFullscreen(false); }} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-slate-500">A IA analisa os documentos enviados e o contexto do negócio antes de aprovar a emissão do contrato.</p>
              <div className="flex items-center justify-between py-2">
                <div><p className="text-sm font-medium text-slate-800">Ativar validação por IA</p><p className="text-[10px] text-slate-400">Exigir aprovação da IA antes de emitir o contrato</p></div>
                <button onClick={() => setAiEnabled(v => !v)} className={`w-10 h-6 rounded-full flex items-center transition-colors px-0.5 ${aiEnabled ? "justify-end" : "bg-slate-300 justify-start"}`} style={aiEnabled ? { backgroundColor: tc500 } : {}}>
                  <div className="w-5 h-5 bg-white rounded-full shadow" />
                </button>
              </div>
              {aiEnabled && (
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-700">Prompt de Validação</label>
                    <button onClick={() => setPromptFullscreen(true)} className="text-slate-400 hover:text-slate-600"><Maximize2 className="w-4 h-4" /></button>
                  </div>
                  <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                    placeholder="Ex: Verifique se o contrato está assinado, se os valores batem..."
                    className="w-full mt-1 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 h-32 resize-y focus:outline-none focus:ring-2" style={{ focusRingColor: tc500 }} />
                  <p className="text-[10px] text-slate-400 mt-1">A IA recebe: documentos enviados + dados do negócio.</p>
                </div>
              )}
              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} onClick={() => { setAiDialogOpen(false); setPromptFullscreen(false); }}>Confirmar</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Pipeline Rules Dialog */}
      {rulesDialogOpen && createPortal(
        <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/50" onClick={() => setRulesDialogOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 sticky top-0 bg-white border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><GitBranch className="w-4 h-4" style={{ color: tc500 }} />Regras de Pipeline</p>
              <button onClick={() => setRulesDialogOpen(false)} className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-slate-500">Selecione em quais etapas do pipeline a aba "Contrato" deve aparecer nos Negócios e defina a regra de obrigatoriedade.</p>
              
              {allPipelines.map(pipe => {
                const stages = allStagesMap[pipe.id] || [];
                const existing = pipelineStages.find(ps => ps.pipeline_id === pipe.id);
                const selectedIds = existing?.stage_ids || [];
                const toggleStage = (stageId) => {
                  setPipelineStages(prev => {
                    const idx = prev.findIndex(ps => ps.pipeline_id === pipe.id);
                    if (idx === -1) {
                      return [...prev, { pipeline_id: pipe.id, stage_ids: [stageId] }];
                    }
                    const curr = [...prev];
                    const sids = curr[idx].stage_ids || [];
                    curr[idx] = { ...curr[idx], stage_ids: sids.includes(stageId) ? sids.filter(s => s !== stageId) : [...sids, stageId] };
                    if (curr[idx].stage_ids.length === 0) return curr.filter((_, i) => i !== idx);
                    return curr;
                  });
                };
                return (
                  <div key={pipe.id} className="border border-slate-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-slate-700">{pipe.name}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {stages.sort((a, b) => (a.order || 0) - (b.order || 0)).map(st => {
                        const isSelected = selectedIds.includes(st.id);
                        return (
                          <button key={st.id} onClick={() => toggleStage(st.id)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors flex items-center gap-1 ${isSelected ? "text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                            style={isSelected ? { backgroundColor: tc500, borderColor: tc500 } : {}}
                            data-testid={`stage-toggle-${st.id}`}>
                            {isSelected && <Check className="w-3 h-3" />}
                            {st.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="pt-2 border-t border-slate-100 space-y-2">
                <p className="text-xs font-semibold text-slate-700">Regra de obrigatoriedade</p>
                <div className="space-y-1.5">
                  {[
                    { value: "none", label: "Sem regra", desc: "Apenas exibe a aba Contrato" },
                    { value: "emitido", label: "Obrigatório contrato emitido", desc: "Bloqueia avanço/ganho sem contrato emitido" },
                    { value: "assinado", label: "Obrigatório contrato assinado", desc: "Bloqueia avanço/ganho sem contrato assinado por todos" },
                    { value: "assinado_externos", label: "Obrigatório assinatura dos externos", desc: "Bloqueia avanço/ganho sem assinatura dos signatários que não são do template" },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setContractRule(opt.value)}
                      className={`w-full text-left p-2.5 rounded-lg border transition-colors ${contractRule === opt.value ? "border-slate-300" : "border-slate-200 hover:border-slate-300"}`}
                      style={contractRule === opt.value ? { backgroundColor: tc50, borderColor: tc500 + "60" } : {}}
                      data-testid={`rule-${opt.value}`}>
                      <p className={`text-xs font-medium ${contractRule === opt.value ? "" : "text-slate-700"}`} style={contractRule === opt.value ? { color: tc500 } : {}}>{opt.label}</p>
                      <p className="text-[10px] text-slate-400">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Button className="w-full text-white" style={{ backgroundColor: tc500 }} onClick={() => setRulesDialogOpen(false)} data-testid="rules-confirm-btn">Confirmar</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Prompt Fullscreen */}
      {promptFullscreen && createPortal(
        <div className="fixed inset-0 z-[9999999] bg-black/90 flex flex-col" onClick={() => setPromptFullscreen(false)}>
          <div className="flex items-center justify-between px-5 py-3 bg-black border-b border-white/10">
            <p className="text-sm text-white/80 flex items-center gap-2"><Brain className="w-4 h-4" />Prompt de Validação IA</p>
            <button onClick={() => setPromptFullscreen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 p-4" onClick={e => e.stopPropagation()}>
            <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
              className="w-full h-full bg-slate-900 text-slate-100 text-sm font-mono p-4 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-slate-500" autoFocus />
          </div>
        </div>,
        document.body
      )}
    </div>,
    document.body
  );
}
